import React, { useEffect, useRef } from 'react'

import * as S from './styles'
import { useScreen } from '@/store/screen'
import { useSmoothScroll } from '@/store/smoothScroll'

export type FloatingIconProps = {
  /**
   * Icon source image
   *
   * @example
   * ```tsx
   * <FloatingIcon src={SvgVariable} />
   * ```
   */
  src: string

  /**
   * Image alt text
   *
   * @example
   * ```tsx
   * <FloatingIcon alt={"floating icon"} />
   * ```
   */
  alt?: string

  /**
   * After the limit (px) is hit, the icon will stop following the screen
   *
   * @example
   * ```tsx
   * <FloatingIcon offsetLimit={1920} />
   * ```
   */
  offsetLimit?: number

  /**
   * Redirect onclick to this link
   *
   * @example
   * ```tsx
   * <FloatingIcon redirect={url} />
   * ```
   *
   */
  redirectUrl: string

  /**
   * Class name of the button
   *
   * @example
   * ```tsx
   * <FloatingIcon className="button-icon" />
   * ```
   *
   */
  className?: string
}

export const FloatingIcon: React.FC<FloatingIconProps> = ({
  src,
  alt = 'icon',
  offsetLimit,
  redirectUrl,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { windowSize } = useScreen()

  const { defaultElement, getScrollbar } = useSmoothScroll()

  const handleClick = () => {
    window.open(redirectUrl, '_blank')
  }

  useEffect(() => {
    const scrollbar = getScrollbar(document.getElementById(defaultElement))
    const container = containerRef.current

    if (!scrollbar || !windowSize || !container) return

    const handleScroll = () => {
      if (offsetLimit) {
        const rect = container.getBoundingClientRect()
        const bottomCoordinate = rect.height + scrollbar.offset.y

        if (bottomCoordinate > offsetLimit) return
      }

      container.style.transform = `translate3d(0, ${scrollbar.offset.y}px, 0px)`
    }

    scrollbar?.addListener(handleScroll)

    // eslint-disable-next-line consistent-return
    return () => {
      scrollbar?.removeListener(handleScroll)
    }
  }, [defaultElement, getScrollbar, offsetLimit, windowSize])

  return (
    <S.FloatingIconContainer ref={containerRef}>
      <S.AlignmentContainer>
        <S.FloatingIcon
          icon={src}
          alt={alt}
          onClick={handleClick}
          className={className}
        />
      </S.AlignmentContainer>
    </S.FloatingIconContainer>
  )
}
